import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Text,
  Stack,
  Group,
  Avatar,
  TextInput,
  Textarea,
  NumberInput,
  PasswordInput,
  Button,
  ActionIcon,
  Alert,
  Progress,
  Badge,
  Loader,
  Center,
  Modal,
  Select,
  Switch,
  SegmentedControl,
  Anchor,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconCheck,
  IconPencil,
  IconUser,
  IconBuilding,
  IconUsers,
  IconTemplate,
  IconPlus,
  IconEdit,
  IconReceipt,
  IconTrash,
  IconMail,
} from '@tabler/icons-react';
import { useAuth } from '../context/AuthContext';
import { useQuoteReadOnly } from '../hooks/useQuoteReadOnly';
import { authApi, shopApi, inspectionApi, priceAgentSettingsApi, type PriceAgentSettings } from '../api/client';
import type { ShopResponse, ShopMemberResponse, TemplateResponse, EmailTemplate } from '../types';
import {
  FLAG_KEYS,
  type FlagKey,
  type Tri,
  useDeviceOverrides,
  setDeviceOverride,
  clearDeviceOverrides,
  hasAnyOverride,
} from '../hooks/featureFlags';

// ─── Design tokens ────────────────────────────────────────────────────────────
const BG = '#0C0C0E';
const CARD_BG = '#18181B';
const BORDER = '#27272A';
const TEXT = '#F4F4F5';
const TEXT_SECONDARY = '#A1A1AA';
const TEXT_MUTED = '#71717A';
const BLUE = '#2563EB';
const NAV_W = 200;

const inputStyles = {
  input: { background: '#111113', borderColor: BORDER, color: TEXT },
  label: { color: TEXT_SECONDARY, marginBottom: 4, fontSize: 13 },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatPhone(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function passwordStrength(pw: string) {
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  return score;
}

const STRENGTH_LABELS = ['', 'Weak', 'Fair', 'Strong', 'Very strong'];
const STRENGTH_COLORS = ['', 'red', 'orange', 'yellow', 'teal'];

// ─── Section wrapper ──────────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box mb={28}>
      <Text size="xs" fw={600} c={TEXT_MUTED} mb={12} style={{ textTransform: 'uppercase', letterSpacing: 0.7 }}>
        {title}
      </Text>
      <Box style={{ background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: 8, overflow: 'hidden' }}>
        {children}
      </Box>
    </Box>
  );
}

// ─── ACCOUNT TAB ─────────────────────────────────────────────────────────────

function ProfileSection() {
  const { user, setUser } = useAuth();
  const initialName = user?.name || '';
  const [name, setName] = useState(initialName);
  const [phone, setPhone] = useState(formatPhone(user?.phone ?? ''));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const isDirty = name !== initialName || phone.replace(/\D/g, '') !== (user?.phone ?? '').replace(/\D/g, '');

  const handleSave = async () => {
    const digits = phone.replace(/\D/g, '');
    if (phone && digits.length !== 10) {
      setError('Phone number must be 10 digits, e.g. (555) 123-4567');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const updated = await authApi.updateMe({
        name: name.trim() || undefined,
        phone: digits || undefined,
      });
      setUser(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : Array.isArray(detail) ? (detail[0]?.msg ?? 'Failed to save.') : 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section title="Profile">
      <Box style={{ padding: '20px 20px 16px', borderBottom: `1px solid ${BORDER}` }}>
        <Group gap={14} align="center">
          <Avatar size={48} radius="xl" color="haist">
            {(initialName[0] ?? user?.name?.[0] ?? '?').toUpperCase()}
          </Avatar>
          <Box>
            <Text size="sm" fw={600} c={TEXT}>{initialName || '—'}</Text>
            <Text size="xs" c={TEXT_MUTED}>{user?.email ?? '—'}</Text>
          </Box>
        </Group>
      </Box>
      <Box p={20}>
        {error && <Alert color="red" icon={<IconAlertCircle size={15} />} radius="sm" mb={16} styles={{ message: { fontSize: 13 } }}>{error}</Alert>}
        <Stack gap={14}>
          <TextInput label="Name" placeholder="Your name" value={name} onChange={(e) => setName(e.currentTarget.value)} styles={inputStyles} leftSection={<IconPencil size={14} color={TEXT_MUTED} />} />
          <TextInput label="Phone" placeholder="(555) 123-4567" value={phone} onChange={(e) => setPhone(formatPhone(e.currentTarget.value))} styles={inputStyles} leftSection={<IconPencil size={14} color={TEXT_MUTED} />} maxLength={14} />
          <TextInput label="Email" value={user?.email ?? ''} disabled description="Email cannot be changed here" styles={{ input: { background: '#111113', borderColor: BORDER, color: TEXT_MUTED, opacity: 0.7 }, label: { color: TEXT_SECONDARY, marginBottom: 4, fontSize: 13 }, description: { fontSize: 11, color: TEXT_MUTED, marginTop: 4 } }} />
          <Group justify="flex-end" mt={4}>
            {saved && <Group gap={6}><IconCheck size={14} color="#22C55E" /><Text size="xs" c="#22C55E">Saved</Text></Group>}
            <Button size="sm" radius="sm" color="haist" onClick={handleSave} loading={saving} disabled={!isDirty}>Save changes</Button>
          </Group>
        </Stack>
      </Box>
    </Section>
  );
}

function PasswordSection() {
  const [current, setCurrent] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const strength = passwordStrength(newPw);

  const handleSave = async () => {
    if (!current) return setError('Enter your current password.');
    if (newPw.length < 8) return setError('New password must be at least 8 characters.');
    if (strength < 3) return setError('Password must include uppercase, number, and special character.');
    if (newPw !== confirm) return setError('Passwords do not match.');
    setSaving(true);
    setError('');
    try {
      await authApi.changePassword({ current_password: current, new_password: newPw });
      setSaved(true);
      setCurrent(''); setNewPw(''); setConfirm('');
      setTimeout(() => setSaved(false), 4000);
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to change password.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section title="Password">
      <Box p={20}>
        {error && <Alert color="red" icon={<IconAlertCircle size={15} />} radius="sm" mb={16} styles={{ message: { fontSize: 13 } }}>{error}</Alert>}
        <Stack gap={14}>
          <PasswordInput label="Current password" placeholder="••••••••" value={current} onChange={(e) => setCurrent(e.currentTarget.value)} styles={inputStyles} />
          <PasswordInput label="New password" placeholder="••••••••" value={newPw} onChange={(e) => setNewPw(e.currentTarget.value)} styles={inputStyles} />
          {newPw.length > 0 && (
            <Box>
              <Progress value={(strength / 4) * 100} color={STRENGTH_COLORS[strength]} size="xs" radius="xl" />
              <Text size="xs" c={STRENGTH_COLORS[strength]} mt={4}>{STRENGTH_LABELS[strength]}</Text>
            </Box>
          )}
          <PasswordInput label="Confirm new password" placeholder="••••••••" value={confirm} onChange={(e) => setConfirm(e.currentTarget.value)} error={confirm && newPw !== confirm ? 'Passwords do not match' : undefined} styles={inputStyles} />
          <Group justify="flex-end" mt={4}>
            {saved && <Group gap={6}><IconCheck size={14} color="#22C55E" /><Text size="xs" c="#22C55E">Password updated</Text></Group>}
            <Button size="sm" radius="sm" color="haist" onClick={handleSave} loading={saving} disabled={!current || !newPw || !confirm}>Update password</Button>
          </Group>
        </Stack>
      </Box>
    </Section>
  );
}

// Per-device override for Quote tab read-only state. Default comes from the
// user's `quote_read_only` role (set by Team Members > Edit > Permissions).
// Override is stored in localStorage and only affects THIS browser.
function QuoteEditingSection() {
  const { userDefault, override, setOverride } = useQuoteReadOnly();
  const value = override ?? 'inherit';
  const defaultLabel = userDefault ? 'Read-only (from your role)' : 'Editable';
  return (
    <Section title="Quote Editing">
      <Box p={20}>
        <Text size="sm" c={TEXT_SECONDARY} mb={16}>
          Default for this account is <strong>{defaultLabel}</strong>. You can override it on this browser only — useful when the same login is shared between a counter PC (read-only) and your laptop (editable).
        </Text>
        <SegmentedControl
          fullWidth
          value={value}
          onChange={(v) => setOverride(v === 'inherit' ? null : (v as 'on' | 'off'))}
          data={[
            { value: 'inherit', label: 'Use default' },
            { value: 'on', label: 'Force read-only' },
            { value: 'off', label: 'Force editable' },
          ]}
          styles={{
            root: { background: '#111113', border: `1px solid ${BORDER}` },
            label: { color: TEXT_SECONDARY, fontSize: 13 },
            indicator: { background: '#27272A' },
          }}
        />
      </Box>
    </Section>
  );
}

function AccountTab() {
  return (
    <>
      <ProfileSection />
      <PasswordSection />
      <QuoteEditingSection />
    </>
  );
}

// ─── SHOP TAB ─────────────────────────────────────────────────────────────────

function ShopTab() {
  const [shop, setShop] = useState<ShopResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  // Basic info
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');

  // Labor rate
  const [laborRate, setLaborRate] = useState<number | string>('');
  const [taxRate, setTaxRate] = useState<number | string>('');
  // Labor rate saved feedback
  const [laborSaving, setLaborSaving] = useState(false);
  const [laborSaved, setLaborSaved] = useState(false);
  const [laborError, setLaborError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const s = await shopApi.get();
      setShop(s);
      setName(s.name ?? '');
      setAddress(s.address ?? '');
      setPhone(formatPhone(s.phone ?? ''));
      setEmail(s.email ?? '');
      setLaborRate(s.labor_rate_default ?? '');
      setTaxRate(s.tax_rate != null ? parseFloat((s.tax_rate * 100).toFixed(3)) : '');
    } catch {
      // no shop
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSaveInfo = async () => {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      const digits = phone.replace(/\D/g, '');
      const updated = await shopApi.update({
        name: name.trim() || undefined,
        address: address.trim() || undefined,
        phone: digits || undefined,
        email: email.trim() || undefined,
      });
      setShop(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveLabor = async () => {
    setLaborSaving(true);
    setLaborError('');
    setLaborSaved(false);
    try {
      const updated = await shopApi.update({
        labor_rate_default: laborRate !== '' ? Number(laborRate) : null,
        tax_rate: taxRate !== '' ? Number(taxRate) / 100 : null,
      });
      setShop(updated);
      setLaborSaved(true);
      setTimeout(() => setLaborSaved(false), 3000);
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      setLaborError(typeof detail === 'string' ? detail : 'Failed to save.');
    } finally {
      setLaborSaving(false);
    }
  };

  if (loading) return <Center py={60}><Loader size="sm" color="gray" /></Center>;
  if (!shop) return <Center py={60}><Text size="sm" c={TEXT_MUTED}>No shop found for this account.</Text></Center>;

  const infoIsDirty = name !== (shop.name ?? '') || address !== (shop.address ?? '') || phone.replace(/\D/g, '') !== (shop.phone ?? '').replace(/\D/g, '') || email !== (shop.email ?? '');

  const numberInput = {
    input: { background: '#111113', borderColor: BORDER, color: TEXT },
    label: { color: TEXT_SECONDARY, marginBottom: 4, fontSize: 13 },
  };

  return (
    <>
      <Section title="Shop Information">
        <Box p={20}>
          {error && <Alert color="red" icon={<IconAlertCircle size={15} />} radius="sm" mb={16} styles={{ message: { fontSize: 13 } }}>{error}</Alert>}
          <Stack gap={14}>
            <TextInput label="Shop name" placeholder="My Auto Shop" value={name} onChange={(e) => setName(e.currentTarget.value)} styles={inputStyles} />
            <TextInput label="Address" placeholder="123 Main St, City, State" value={address} onChange={(e) => setAddress(e.currentTarget.value)} styles={inputStyles} />
            <TextInput label="Phone" placeholder="(555) 123-4567" value={phone} onChange={(e) => setPhone(formatPhone(e.currentTarget.value))} styles={inputStyles} maxLength={14} />
            <TextInput label="Email" placeholder="shop@example.com" value={email} onChange={(e) => setEmail(e.currentTarget.value)} styles={inputStyles} />
            <Group justify="flex-end" mt={4}>
              {saved && <Group gap={6}><IconCheck size={14} color="#22C55E" /><Text size="xs" c="#22C55E">Saved</Text></Group>}
              <Button size="sm" radius="sm" color="haist" onClick={handleSaveInfo} loading={saving} disabled={!infoIsDirty}>Save changes</Button>
            </Group>
          </Stack>
        </Box>
      </Section>

      <Section title="Labor & Pricing">
        <Box p={20}>
          {laborError && <Alert color="red" icon={<IconAlertCircle size={15} />} radius="sm" mb={16} styles={{ message: { fontSize: 13 } }}>{laborError}</Alert>}
          <Stack gap={20}>

            {/* Labor rate */}
            <Box>
              <Text size="xs" fw={600} c={TEXT_SECONDARY} mb={10} style={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Labor Rate</Text>
              <NumberInput
                label="Standard rate"
                placeholder="145"
                value={laborRate}
                onChange={setLaborRate}
                min={0}
                max={9999}
                prefix="$"
                suffix=" /hr"
                decimalScale={2}
                styles={numberInput}
                style={{ width: 160 }}
              />
            </Box>

            {/* Parts */}
            <Box>
              <Text size="xs" fw={600} c={TEXT_SECONDARY} mb={10} style={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Parts</Text>
              <NumberInput
                label="Tax rate"
                placeholder="8.5"
                value={taxRate}
                onChange={setTaxRate}
                min={0}
                max={50}
                suffix="%"
                decimalScale={1}
                styles={numberInput}
                style={{ width: 120 }}
              />
            </Box>

            <Group justify="flex-end" mt={4}>
              {laborSaved && <Group gap={6}><IconCheck size={14} color="#22C55E" /><Text size="xs" c="#22C55E">Saved</Text></Group>}
              <Button size="sm" radius="sm" color="haist" leftSection={<IconReceipt size={14} />} onClick={handleSaveLabor} loading={laborSaving}>Save rates</Button>
            </Group>
          </Stack>
        </Box>
      </Section>
    </>
  );
}

// ─── TEAM TAB ─────────────────────────────────────────────────────────────────

const ROLE_OPTIONS = [
  { value: 'shop_owner', label: 'Shop Owner' },
  { value: 'service_advisor', label: 'Service Advisor' },
  { value: 'technician', label: 'Technician' },
  { value: 'manager', label: 'Manager' },
];

// Permission flags stored as extra role strings (not pickable as primary role)
const STAGE_MANAGER_ROLE = 'stage_manager';
const QUOTE_READ_ONLY_ROLE = 'quote_read_only';
// Roles that are "modifier" flags rather than primary role choices —
// excluded from the primary-role chip / Select but rendered as toggles.
const MODIFIER_ROLES = [STAGE_MANAGER_ROLE, QUOTE_READ_ONLY_ROLE];

function roleLabel(role: string) {
  return ROLE_OPTIONS.find((r) => r.value === role)?.label ?? role;
}

function MemberRow({ member, onEdit }: { member: ShopMemberResponse; onEdit: (m: ShopMemberResponse) => void }) {
  const displayName = member.name;
  const initials = (displayName[0] ?? '?').toUpperCase();

  return (
    <Box style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px', borderBottom: `1px solid ${BORDER}` }}>
      <Avatar size={34} radius="xl" color="haist">{initials}</Avatar>
      <Box style={{ flex: 1, minWidth: 0 }}>
        <Text size="sm" fw={600} c={TEXT} truncate>{displayName}</Text>
        <Text size="xs" c={TEXT_MUTED} truncate>{member.email ?? '—'}</Text>
      </Box>
      <Group gap={8} style={{ flexShrink: 0 }}>
        {member.roles.filter((r) => !MODIFIER_ROLES.includes(r)).map((r) => (
          <Badge key={r} size="xs" variant="light" color="blue" style={{ textTransform: 'none' }}>{roleLabel(r)}</Badge>
        ))}
        {member.roles.includes(STAGE_MANAGER_ROLE) && (
          <Badge size="xs" variant="light" color="green" style={{ textTransform: 'none' }}>Stage</Badge>
        )}
        {member.roles.includes(QUOTE_READ_ONLY_ROLE) && (
          <Badge size="xs" variant="light" color="orange" style={{ textTransform: 'none' }}>Quote R/O</Badge>
        )}
        {!member.is_active && <Badge size="xs" variant="light" color="gray">Inactive</Badge>}
      </Group>
      <Button size="xs" variant="subtle" color="gray" leftSection={<IconEdit size={12} />} onClick={() => onEdit(member)}>Edit</Button>
    </Box>
  );
}

function TeamTab() {
  const [members, setMembers] = useState<ShopMemberResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editMember, setEditMember] = useState<ShopMemberResponse | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await shopApi.listMembers();
      setMembers(list);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <>
      <Section title="Team Members">
        <Box style={{ padding: '12px 20px', borderBottom: `1px solid ${BORDER}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text size="sm" c={TEXT_SECONDARY}>{members.length} member{members.length !== 1 ? 's' : ''}</Text>
          <Button size="xs" color="haist" leftSection={<IconPlus size={12} />} onClick={() => setInviteOpen(true)}>Add member</Button>
        </Box>
        {loading ? (
          <Center py={40}><Loader size="sm" color="gray" /></Center>
        ) : members.length === 0 ? (
          <Center py={40}><Text size="sm" c={TEXT_MUTED}>No team members yet.</Text></Center>
        ) : (
          members.map((m) => <MemberRow key={m.id} member={m} onEdit={setEditMember} />)
        )}
      </Section>

      <InviteMemberModal opened={inviteOpen} onClose={() => setInviteOpen(false)} onCreated={(m) => { setMembers((prev) => [...prev, m]); setInviteOpen(false); }} />
      {editMember && (
        <EditMemberModal
          member={editMember}
          onClose={() => setEditMember(null)}
          onUpdated={(m) => { setMembers((prev) => prev.map((x) => x.id === m.id ? m : x)); setEditMember(null); }}
        />
      )}
    </>
  );
}

function InviteMemberModal({ opened, onClose, onCreated }: { opened: boolean; onClose: () => void; onCreated: (m: ShopMemberResponse) => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('shop_owner');
  const [canManageStage, setCanManageStage] = useState(true);
  const [quoteReadOnly, setQuoteReadOnly] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const reset = () => { setName(''); setEmail(''); setRole('shop_owner'); setCanManageStage(true); setQuoteReadOnly(false); setError(''); };

  const handleSubmit = async () => {
    if (!name.trim() || !email.trim()) return setError('Name and email are required.');
    setSaving(true);
    setError('');
    try {
      const roles = [role];
      if (canManageStage) roles.push(STAGE_MANAGER_ROLE);
      if (quoteReadOnly) roles.push(QUOTE_READ_ONLY_ROLE);
      const m = await shopApi.createMember({ email: email.trim(), name: name.trim(), roles });
      onCreated(m);
      reset();
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to create member.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal opened={opened} onClose={() => { onClose(); reset(); }} title={<Text size="sm" fw={600} c={TEXT}>Add Team Member</Text>} styles={{ content: { background: CARD_BG, border: `1px solid ${BORDER}` }, header: { background: CARD_BG }, overlay: { backdropFilter: 'blur(4px)' } }} radius="md">
      <Stack gap={12}>
        {error && <Alert color="red" icon={<IconAlertCircle size={15} />} radius="sm" styles={{ message: { fontSize: 13 } }}>{error}</Alert>}
        <TextInput label="Name" placeholder="Jane Smith" value={name} onChange={(e) => setName(e.currentTarget.value)} styles={inputStyles} />
        <TextInput label="Email" placeholder="jane@shop.com" value={email} onChange={(e) => setEmail(e.currentTarget.value)} styles={inputStyles} />
        <Select
          label="Role"
          data={ROLE_OPTIONS}
          value={role}
          onChange={(v) => setRole(v ?? 'shop_owner')}
          styles={{ input: { background: '#111113', borderColor: BORDER, color: TEXT }, label: { color: TEXT_SECONDARY, marginBottom: 4, fontSize: 13 } }}
          comboboxProps={{ withinPortal: true, styles: { dropdown: { background: '#1C1C1F', border: `1px solid ${BORDER}` }, option: { color: TEXT, fontSize: 13 } } }}
        />
        <Text size="xs" c={TEXT_MUTED}>Default password: <code style={{ color: TEXT_SECONDARY }}>qwer1234</code> — member should change it on first login.</Text>
        <Box style={{ borderTop: `1px solid ${BORDER}`, paddingTop: 12 }}>
          <Text size="xs" fw={600} c={TEXT_SECONDARY} mb={8} style={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>Permissions</Text>
          <Switch
            label="Can change conversation stage"
            description="Advance / revert workflow stage (registration → inspection → quote → completed)"
            checked={canManageStage}
            onChange={(e) => setCanManageStage(e.currentTarget.checked)}
            color="haist"
            styles={{ label: { color: TEXT, fontSize: 13 }, description: { color: TEXT_MUTED, fontSize: 12 } }}
          />
          <Switch
            mt={8}
            label="Quote read-only"
            description="Default Job (quote) tab to view-only across this user's devices. Signing / approvals still work."
            checked={quoteReadOnly}
            onChange={(e) => setQuoteReadOnly(e.currentTarget.checked)}
            color="haist"
            styles={{ label: { color: TEXT, fontSize: 13 }, description: { color: TEXT_MUTED, fontSize: 12 } }}
          />
        </Box>
        <Group justify="flex-end" mt={4}>
          <Button size="sm" variant="subtle" color="gray" onClick={() => { onClose(); reset(); }}>Cancel</Button>
          <Button size="sm" color="haist" onClick={handleSubmit} loading={saving}>Add member</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function EditMemberModal({ member, onClose, onUpdated }: { member: ShopMemberResponse; onClose: () => void; onUpdated: (m: ShopMemberResponse) => void }) {
  const initialPrimary = member.roles.find((r) => !MODIFIER_ROLES.includes(r)) ?? 'shop_owner';
  const [role, setRole] = useState(initialPrimary);
  const [canManageStage, setCanManageStage] = useState(member.roles.includes(STAGE_MANAGER_ROLE));
  const [quoteReadOnly, setQuoteReadOnly] = useState(member.roles.includes(QUOTE_READ_ONLY_ROLE));
  const [isActive, setIsActive] = useState(member.is_active);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const displayName = member.name;

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const roles = [role];
      if (canManageStage) roles.push(STAGE_MANAGER_ROLE);
      if (quoteReadOnly) roles.push(QUOTE_READ_ONLY_ROLE);
      const updated = await shopApi.updateMember(member.id, { roles, is_active: isActive });
      onUpdated(updated);
    } catch (e: unknown) {
      const detail = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Failed to update member.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal opened onClose={onClose} title={<Text size="sm" fw={600} c={TEXT}>Edit {displayName}</Text>} styles={{ content: { background: CARD_BG, border: `1px solid ${BORDER}` }, header: { background: CARD_BG }, overlay: { backdropFilter: 'blur(4px)' } }} radius="md">
      <Stack gap={12}>
        {error && <Alert color="red" icon={<IconAlertCircle size={15} />} radius="sm" styles={{ message: { fontSize: 13 } }}>{error}</Alert>}
        <Select
          label="Role"
          data={ROLE_OPTIONS}
          value={role}
          onChange={(v) => setRole(v ?? 'shop_owner')}
          styles={{ input: { background: '#111113', borderColor: BORDER, color: TEXT }, label: { color: TEXT_SECONDARY, marginBottom: 4, fontSize: 13 } }}
          comboboxProps={{ withinPortal: true, styles: { dropdown: { background: '#1C1C1F', border: `1px solid ${BORDER}` }, option: { color: TEXT, fontSize: 13 } } }}
        />
        <Select
          label="Status"
          data={[{ value: 'true', label: 'Active' }, { value: 'false', label: 'Inactive' }]}
          value={String(isActive)}
          onChange={(v) => setIsActive(v === 'true')}
          styles={{ input: { background: '#111113', borderColor: BORDER, color: TEXT }, label: { color: TEXT_SECONDARY, marginBottom: 4, fontSize: 13 } }}
          comboboxProps={{ withinPortal: true, styles: { dropdown: { background: '#1C1C1F', border: `1px solid ${BORDER}` }, option: { color: TEXT, fontSize: 13 } } }}
        />
        <Box style={{ borderTop: `1px solid ${BORDER}`, paddingTop: 12 }}>
          <Text size="xs" fw={600} c={TEXT_SECONDARY} mb={8} style={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>Permissions</Text>
          <Switch
            label="Can change conversation stage"
            description="Advance / revert workflow stage (registration → inspection → quote → completed)"
            checked={canManageStage}
            onChange={(e) => setCanManageStage(e.currentTarget.checked)}
            color="haist"
            styles={{ label: { color: TEXT, fontSize: 13 }, description: { color: TEXT_MUTED, fontSize: 12 } }}
          />
          <Switch
            mt={8}
            label="Quote read-only"
            description="Default Job (quote) tab to view-only across this user's devices. Signing / approvals still work. Per-device override is available on the quote tab."
            checked={quoteReadOnly}
            onChange={(e) => setQuoteReadOnly(e.currentTarget.checked)}
            color="haist"
            styles={{ label: { color: TEXT, fontSize: 13 }, description: { color: TEXT_MUTED, fontSize: 12 } }}
          />
        </Box>
        <Group justify="flex-end" mt={4}>
          <Button size="sm" variant="subtle" color="gray" onClick={onClose}>Cancel</Button>
          <Button size="sm" color="haist" onClick={handleSave} loading={saving}>Save</Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ─── DEFAULT EMAIL TEMPLATES ─────────────────────────────────────────────────

const DEFAULT_EMAIL_TEMPLATES: EmailTemplate[] = [
  {
    id: 'default-inspection-complete',
    name: 'Service Report',
    subject: '{{shop_name}} - Your {{vehicle}} Service Report',
    body: 'Hi {{customer_name}},\n\nYour {{vehicle}} service report is ready for review.\n\nThe report includes our findings, condition photos where available, and recommended next steps with estimated costs.\n\nIf you have any questions, don\'t hesitate to give us a call.\n\nThank you for choosing {{shop_name}}!',
    is_default: true,
  },
  {
    id: 'default-followup',
    name: 'Follow-up Reminder',
    subject: '{{shop_name}} - Service Follow-up for {{vehicle}}',
    body: 'Hi {{customer_name}},\n\nWe wanted to follow up on your recent {{vehicle}} service report.\n\nThere are a few recommended items we\'d love to help you take care of before they become bigger issues. You can review everything in your report using the link below.\n\nFeel free to reach out anytime to schedule — we\'re happy to help.\n\nBest regards,\n{{shop_name}}',
    is_default: false,
  },
];

// ─── EMAIL TEMPLATES SECTION ─────────────────────────────────────────────────

function EmailTemplatesSection() {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  useEffect(() => {
    shopApi.get().then((shop) => {
      const tpls = shop.email_templates && shop.email_templates.length > 0
        ? shop.email_templates
        : DEFAULT_EMAIL_TEMPLATES;
      setTemplates(tpls);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await shopApi.update({ email_templates: templates });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {}
    setSaving(false);
  }, [templates]);

  const addTemplate = () => {
    const newTpl: EmailTemplate = {
      id: crypto.randomUUID(),
      name: 'New Template',
      subject: '{{shop_name}} - Vehicle Inspection Report',
      body: 'Hi {{customer_name}},\n\nYour inspection report for your {{vehicle}} is ready.\n\nThank you,\n{{shop_name}}',
    };
    setTemplates([...templates, newTpl]);
    setEditingId(newTpl.id);
  };

  const updateTemplate = (id: string, updates: Partial<EmailTemplate>) => {
    setTemplates(templates.map((t) => t.id === id ? { ...t, ...updates } : t));
  };

  const deleteTemplate = (id: string) => {
    setTemplates(templates.filter((t) => t.id !== id));
    if (editingId === id) setEditingId(null);
  };

  if (loading) return <Section title="Email Templates"><Center py={40}><Loader size="sm" color="gray" /></Center></Section>;

  return (
    <Section title="Email Templates">
      <Stack gap={0}>
        {templates.map((tpl, i) => {
          const editing = editingId === tpl.id;
          return (
            <Box key={tpl.id} style={{ borderBottom: i < templates.length - 1 ? `1px solid ${BORDER}` : 'none' }}>
              <Box
                style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px', cursor: 'pointer' }}
                onClick={() => setEditingId(editing ? null : tpl.id)}
              >
                <Box style={{ width: 28, height: 28, borderRadius: 6, background: 'rgba(37,99,235,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <IconMail size={14} color={BLUE} />
                </Box>
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Text size="sm" fw={500} c={TEXT} truncate>{tpl.name}</Text>
                  <Text size="xs" c={TEXT_MUTED} truncate>{tpl.subject}</Text>
                </Box>
                <ActionIcon variant="subtle" color="red" size="sm" onClick={(e) => { e.stopPropagation(); deleteTemplate(tpl.id); }}>
                  <IconTrash size={14} />
                </ActionIcon>
              </Box>
              {editing && (
                <Box style={{ padding: '0 20px 16px' }}>
                  <Stack gap={10}>
                    <TextInput
                      label="Template Name"
                      value={tpl.name}
                      onChange={(e) => updateTemplate(tpl.id, { name: e.currentTarget.value })}
                      styles={inputStyles}
                      size="sm"
                    />
                    <TextInput
                      label="Subject"
                      value={tpl.subject}
                      onChange={(e) => updateTemplate(tpl.id, { subject: e.currentTarget.value })}
                      styles={inputStyles}
                      size="sm"
                    />
                    <Textarea
                      label="Message"
                      value={tpl.body}
                      onChange={(e) => updateTemplate(tpl.id, { body: e.currentTarget.value })}
                      styles={inputStyles}
                      size="sm"
                      minRows={6}
                      autosize
                      placeholder="Hi {{customer_name}},&#10;&#10;Your inspection report is ready..."
                    />
                    <Text size="xs" c={TEXT_MUTED}>
                      Variables: {'{{customer_name}}'}, {'{{vehicle}}'}, {'{{shop_name}}'}, {'{{shop_phone}}'}, {'{{shop_email}}'}
                    </Text>
                    <Text size="xs" c={TEXT_MUTED} mt={-6}>
                      A "View Report" button is automatically added to every email.
                    </Text>
                  </Stack>
                </Box>
              )}
            </Box>
          );
        })}
      </Stack>
      <Group mt={12} px={20} pb={16} justify="space-between">
        <Button variant="subtle" size="xs" color="blue" leftSection={<IconPlus size={14} />} onClick={addTemplate}>
          Add Template
        </Button>
        <Group gap={8}>
          {saved && <Text size="xs" c="green" fw={500}>Saved</Text>}
          <Button size="xs" color="blue" onClick={handleSave} loading={saving}>Save Templates</Button>
        </Group>
      </Group>
    </Section>
  );
}

// ─── TEMPLATES TAB ────────────────────────────────────────────────────────────

function TemplatesTab() {
  const [templates, setTemplates] = useState<TemplateResponse[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    inspectionApi.getTemplates()
      .then(setTemplates)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <Stack gap={20}>
      <Section title="Inspection Templates">
        {loading ? (
          <Center py={40}><Loader size="sm" color="gray" /></Center>
        ) : templates.length === 0 ? (
          <Center py={40}><Text size="sm" c={TEXT_MUTED}>No templates found.</Text></Center>
        ) : (
          templates.map((t, i) => (
            <Box key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px', borderBottom: i < templates.length - 1 ? `1px solid ${BORDER}` : 'none' }}>
              <Box style={{ width: 28, height: 28, borderRadius: 6, background: 'rgba(37,99,235,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <IconTemplate size={14} color={BLUE} />
              </Box>
              <Box style={{ flex: 1, minWidth: 0 }}>
                <Text size="sm" fw={500} c={TEXT} truncate>{t.name}</Text>
                {t.items_count != null && (
                  <Text size="xs" c={TEXT_MUTED}>{t.items_count} item{t.items_count !== 1 ? 's' : ''}</Text>
                )}
              </Box>
              <Group gap={6}>
                {t.is_active === false && <Badge size="xs" variant="light" color="gray">Inactive</Badge>}
              </Group>
            </Box>
          ))
        )}
      </Section>
      <EmailTemplatesSection />
    </Stack>
  );
}

// ─── Nav items ────────────────────────────────────────────────────────────────

// ─── FEATURES TAB ───────────────────────────────────────────────────────────

const FEATURE_ITEMS: { key: string; label: string; desc: string }[] = [
  { key: 'inspect', label: 'Inspect', desc: 'AI-powered vehicle inspection with photo analysis' },
  { key: 'repair', label: 'Repair', desc: 'Chat with AI assistant for diagnosis and repair guidance' },
  { key: 'quote', label: 'Quote', desc: 'Generate and manage repair quotes for customers' },
  { key: 'sms', label: 'SMS', desc: 'Send reports and messages to customers via SMS' },
  { key: 'rec', label: 'Job Recommendations', desc: 'On-creation AI job recommendations in chat (dev/opt-in — set device to On)' },
];

function FeaturesTab() {
  const [saving, setSaving] = useState(false);
  const [shopId, setShopId] = useState<number | null>(null);
  const [flags, setFlags] = useState<Record<string, Record<string, boolean>>>({ web: {}, mobile: {}, extension: {} });
  const deviceOverrides = useDeviceOverrides(shopId);
  const hasOverrides = hasAnyOverride(deviceOverrides);

  useEffect(() => {
    shopApi.get().then((shop) => {
      const ff = shop.feature_flags || {};
      const defaults = { inspect: true, repair: true, quote: true, sms: true };
      setShopId(shop.id);
      setFlags({
        web: { ...defaults, ...(ff.web || {}) },
        mobile: { ...defaults, ...(ff.mobile || {}) },
        extension: { ...defaults, ...(ff.extension || {}) },
      });
    }).catch(() => {});
  }, []);

  const handleToggle = async (platform: 'web' | 'mobile' | 'extension', key: string) => {
    const next = { ...flags, [platform]: { ...flags[platform], [key]: !flags[platform][key] } };
    setFlags(next);
    setSaving(true);
    try {
      await shopApi.update({ feature_flags: next } as any);
    } catch {}
    finally { setSaving(false); }
  };

  const handleOverrideChange = (platform: 'web' | 'extension', key: FlagKey, value: string) => {
    const tri: Tri | null = value === 'inherit' ? null : (value as Tri);
    setDeviceOverride(shopId, platform, key, tri);
  };

  const renderRow = (platform: 'web' | 'mobile' | 'extension', f: { key: string; label: string; desc: string }, isLast: boolean) => {
    const shopOn = flags[platform]?.[f.key] !== false;
    const supportsDevice = platform === 'web' || platform === 'extension';
    const override = supportsDevice ? deviceOverrides[platform as 'web' | 'extension']?.[f.key as FlagKey] : undefined;
    const overrideValue = override ?? 'inherit';
    const conflict = supportsDevice && override === 'on' && !shopOn;

    return (
      <Box
        key={f.key}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
          padding: '14px 20px', borderBottom: isLast ? 'none' : `1px solid ${BORDER}`,
        }}
      >
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Text size="sm" fw={600} c={TEXT}>{f.label}</Text>
          <Text size="xs" c={TEXT_MUTED}>{f.desc}</Text>
          {conflict && (
            <Text size="xs" c="orange" mt={4}>
              Override active — shop has this disabled, API will refuse writes
            </Text>
          )}
        </Box>
        <Box style={{ display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0 }}>
          {supportsDevice && (
            <SegmentedControl
              size="xs"
              value={overrideValue}
              onChange={(v) => handleOverrideChange(platform as 'web' | 'extension', f.key as FlagKey, v)}
              data={[
                { value: 'inherit', label: 'Inherit' },
                { value: 'on', label: 'On' },
                { value: 'off', label: 'Off' },
              ]}
              styles={{
                root: { background: '#111113', border: `1px solid ${BORDER}` },
                label: { color: TEXT_SECONDARY, fontSize: 11, padding: '2px 8px' },
                indicator: { background: '#27272A' },
              }}
            />
          )}
          <Box
            onClick={() => handleToggle(platform, f.key)}
            style={{
              width: 44, height: 24, borderRadius: 12, cursor: 'pointer', transition: 'background 0.2s',
              background: shopOn ? BLUE : '#3F3F46',
              display: 'flex', alignItems: 'center', padding: 2, flexShrink: 0,
              opacity: saving ? 0.5 : 1,
            }}
          >
            <Box style={{
              width: 20, height: 20, borderRadius: 10, background: '#fff',
              transition: 'transform 0.2s',
              transform: shopOn ? 'translateX(20px)' : 'translateX(0)',
            }} />
          </Box>
        </Box>
      </Box>
    );
  };

  const renderPlatform = (platform: 'web' | 'mobile' | 'extension', label: string) => {
    const supportsDevice = platform === 'web' || platform === 'extension';
    return (
      <Section title={`${label} Features`}>
        {supportsDevice && (
          <Box style={{ padding: '10px 20px', borderBottom: `1px solid ${BORDER}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
            <Text size="xs" c={TEXT_MUTED}>Shop default applies to all devices. <strong>Inherit / On / Off</strong> overrides apply only to this device (saved in this browser).</Text>
            <Box style={{ display: 'flex', gap: 14, flexShrink: 0 }}>
              <Text size="xs" c={TEXT_MUTED} style={{ width: 168, textAlign: 'center' }}>This device only</Text>
              <Text size="xs" c={TEXT_MUTED} style={{ width: 44, textAlign: 'center' }}>Shop</Text>
            </Box>
          </Box>
        )}
        <Stack gap={0}>
          {FEATURE_ITEMS.map((f, i) => renderRow(platform, f, i === FEATURE_ITEMS.length - 1))}
        </Stack>
      </Section>
    );
  };

  return (
    <>
      {hasOverrides && (
        <Alert color="blue" icon={<IconAlertCircle size={15} />} radius="sm" mb={16} styles={{ message: { fontSize: 13 } }}>
          Device overrides active on this browser.{' '}
          <Anchor size="sm" onClick={() => clearDeviceOverrides(shopId)} style={{ cursor: 'pointer' }}>
            Clear all
          </Anchor>
        </Alert>
      )}
      {renderPlatform('web', 'Web')}
      {renderPlatform('extension', 'Chrome Extension')}
      {renderPlatform('mobile', 'Mobile')}
    </>
  );
}

type Tab = 'account' | 'shop' | 'team' | 'templates' | 'report' | 'features' | 'price_agent';

const NAV_ITEMS: { value: Tab; label: string; icon: React.ReactNode }[] = [
  { value: 'account', label: 'My Account', icon: <IconUser size={15} /> },
  { value: 'shop', label: 'My Shop', icon: <IconBuilding size={15} /> },
  { value: 'team', label: 'Team Members', icon: <IconUsers size={15} /> },
  { value: 'features', label: 'Features', icon: <IconTemplate size={15} /> },
  { value: 'templates', label: 'Templates', icon: <IconTemplate size={15} /> },
  { value: 'report', label: 'Report', icon: <IconReceipt size={15} /> },
  { value: 'price_agent', label: 'Price Agent', icon: <IconReceipt size={15} /> },
];

// ─── REPORT TAB ──────────────────────────────────────────────────────────────

interface ReportConfig {
  show_repair_scope: boolean;
  show_parts_preference: boolean;
  show_comments: boolean;
  show_quote_approval: boolean;
  show_review_nudge: boolean;
  review_qr: boolean;
  signature_required: boolean;
  google_review_url: string;
  yelp_review_url: string;
  enabled_stages: string[];
  sms_templates: Record<string, string>;
  [key: string]: boolean | string | string[] | Record<string, string>;
}

const DEFAULT_SMS_TEMPLATES: Record<string, string> = {
  inspection: "Hi {customer}, this is {shop}. Your {vehicle} has been checked in and we're starting the inspection now. View details: {report_url}",
  quote: "Hi {customer}, your {vehicle} inspection is complete! We've put together a quote for you. Review and approve: {report_url}",
  completed: "Hi {customer}, great news — your {vehicle} is all done and ready for pickup! {report_url}",
};

const DEFAULT_REPORT_CONFIG: ReportConfig = {
  show_repair_scope: true,
  show_parts_preference: true,
  show_comments: true,
  show_quote_approval: true,
  show_review_nudge: false,
  review_qr: false,
  signature_required: false,
  google_review_url: '',
  yelp_review_url: '',
  enabled_stages: ['registration', 'inspection', 'quote', 'completed'],
  sms_templates: { ...DEFAULT_SMS_TEMPLATES },
};

function ReportTab() {
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState<ReportConfig>(DEFAULT_REPORT_CONFIG);

  useEffect(() => {
    shopApi.get().then((shop) => {
      if (shop.report_config) {
        setConfig((prev) => ({ ...prev, ...shop.report_config } as ReportConfig));
      }
    }).catch(() => {});
  }, []);

  async function handleToggle(key: string) {
    const next = { ...config, [key]: !config[key as keyof typeof config] };
    setConfig(next);
    setSaving(true);
    try {
      await shopApi.update({ report_config: next });
    } catch {}
    finally { setSaving(false); }
  }

  const toggles = [
    { key: 'show_repair_scope', label: 'What would you like to fix?', desc: 'Customer selects repair scope: Peak Condition, Reliability & Safety, or Safety-Critical Only' },
    { key: 'show_parts_preference', label: 'Parts preference', desc: 'Customer selects OEM, OE Equivalent, or Economy parts' },
    { key: 'show_comments', label: 'Questions or comments', desc: 'Free-text field for customer notes, budget info, or questions' },
    { key: 'show_quote_approval', label: 'Approve Your Quote', desc: 'Customer can approve or decline the repair quote directly from the report' },
    { key: 'signature_required', label: 'Require signature for approval', desc: 'When on, approvals must include a digital signature (customer portal) or an uploaded photo of a signed paper quote' },
  ];

  const allOff = !config.show_repair_scope && !config.show_parts_preference && !config.show_comments;

  return (
    <>
      <Section title="Customer Report Preferences">
        <Text size="sm" c={TEXT_SECONDARY} mb={16}>
          Control which preference sections appear on the customer-facing report. If all are turned off, the entire preferences section is hidden.
        </Text>
        <Stack gap={12}>
          {toggles.map((t) => (
            <Box
              key={t.key}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 16px', background: '#111113', borderRadius: 8, border: `1px solid ${BORDER}`,
              }}
            >
              <Box>
                <Text size="sm" fw={600} c={TEXT}>{t.label}</Text>
                <Text size="xs" c={TEXT_MUTED}>{t.desc}</Text>
              </Box>
              <Box
                onClick={() => handleToggle(t.key)}
                style={{
                  width: 44, height: 24, borderRadius: 12, cursor: 'pointer', transition: 'background 0.2s',
                  background: config[t.key as keyof typeof config] ? BLUE : '#3F3F46',
                  display: 'flex', alignItems: 'center', padding: 2, flexShrink: 0, marginLeft: 16,
                  opacity: saving ? 0.5 : 1,
                }}
              >
                <Box style={{
                  width: 20, height: 20, borderRadius: 10, background: '#fff',
                  transition: 'transform 0.2s',
                  transform: config[t.key as keyof typeof config] ? 'translateX(20px)' : 'translateX(0)',
                }} />
              </Box>
            </Box>
          ))}
        </Stack>
        {allOff && (
          <Alert color="yellow" mt={12} styles={{ root: { background: 'rgba(234,179,8,0.08)', border: `1px solid rgba(234,179,8,0.2)` }, message: { color: TEXT_SECONDARY } }}>
            All preference sections are off — the entire "Tell Us Your Preferences" block will be hidden from customers.
          </Alert>
        )}
      </Section>

      <Section title="Review Nudge">
        <Text size="sm" c={TEXT_SECONDARY} mb={16}>
          Encourage customers to leave a Google or Yelp review at the bottom of their report.
        </Text>

        <Box
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 16px', background: '#111113', borderRadius: 8, border: `1px solid ${BORDER}`, marginBottom: 12,
          }}
        >
          <Box>
            <Text size="sm" fw={600} c={TEXT}>Show review request</Text>
            <Text size="xs" c={TEXT_MUTED}>Display "How was your experience?" with review buttons</Text>
          </Box>
          <Box
            onClick={() => handleToggle('show_review_nudge')}
            style={{
              width: 44, height: 24, borderRadius: 12, cursor: 'pointer', transition: 'background 0.2s',
              background: config.show_review_nudge ? BLUE : '#3F3F46',
              display: 'flex', alignItems: 'center', padding: 2, flexShrink: 0, marginLeft: 16,
              opacity: saving ? 0.5 : 1,
            }}
          >
            <Box style={{
              width: 20, height: 20, borderRadius: 10, background: '#fff',
              transition: 'transform 0.2s',
              transform: config.show_review_nudge ? 'translateX(20px)' : 'translateX(0)',
            }} />
          </Box>
        </Box>

        {config.show_review_nudge && (
          <Stack gap={10}>
            <TextInput
              label="Google Review URL"
              placeholder="https://maps.app.goo.gl/..."
              value={config.google_review_url || ''}
              onChange={(e) => setConfig((c: ReportConfig) => ({ ...c, google_review_url: e.currentTarget.value }))}
              onBlur={() => { setSaving(true); shopApi.update({ report_config: config }).finally(() => setSaving(false)); }}
              styles={inputStyles}
            />
            <TextInput
              label="Yelp Review URL"
              placeholder="https://www.yelp.com/biz/..."
              value={config.yelp_review_url || ''}
              onChange={(e) => setConfig((c: ReportConfig) => ({ ...c, yelp_review_url: e.currentTarget.value }))}
              onBlur={() => { setSaving(true); shopApi.update({ report_config: config }).finally(() => setSaving(false)); }}
              styles={inputStyles}
            />
            <Box
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 14px', background: '#111113', borderRadius: 8, border: `1px solid ${BORDER}`,
              }}
            >
              <Box>
                <Text size="sm" fw={600} c={TEXT}>Show review QR codes</Text>
                <Text size="xs" c={TEXT_MUTED}>Scannable Google/Yelp QR on the report — great for a counter tablet</Text>
              </Box>
              <Box
                onClick={() => handleToggle('review_qr')}
                style={{
                  width: 44, height: 24, borderRadius: 12, cursor: 'pointer', transition: 'background 0.2s',
                  background: config.review_qr ? BLUE : '#3F3F46',
                  display: 'flex', alignItems: 'center', padding: 2, flexShrink: 0, marginLeft: 16,
                  opacity: saving ? 0.5 : 1,
                }}
              >
                <Box style={{
                  width: 20, height: 20, borderRadius: 10, background: '#fff',
                  transition: 'transform 0.2s',
                  transform: config.review_qr ? 'translateX(20px)' : 'translateX(0)',
                }} />
              </Box>
            </Box>
          </Stack>
        )}
      </Section>

      <Section title="Workflow Stages">
        <Text size="sm" c={TEXT_SECONDARY} mb={16}>
          Enable or disable workflow stages. Disabled stages will be skipped during transitions and hidden from customer reports.
        </Text>
        <Stack gap={8}>
          {[
            { key: 'registration', label: 'Check-In', desc: 'Vehicle intake, recalls, customer concerns', locked: true },
            { key: 'inspection', label: 'Inspection', desc: 'Multi-point inspection with photos and findings' },
            { key: 'quote', label: 'Quote', desc: 'Service quote with approve/decline for customer' },
            { key: 'completed', label: 'Complete', desc: 'Service complete, review request', locked: true },
          ].map((stage) => {
            const enabled = (config.enabled_stages || ['registration', 'inspection', 'quote', 'completed']).includes(stage.key);
            return (
              <Box key={stage.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: '#111113', borderRadius: 8, border: `1px solid ${BORDER}` }}>
                <Box>
                  <Text size="sm" fw={600} c={TEXT}>{stage.label}</Text>
                  <Text size="xs" c={TEXT_MUTED}>{stage.desc}</Text>
                </Box>
                {stage.locked ? (
                  <Text size="xs" c={TEXT_MUTED} style={{ flexShrink: 0, marginLeft: 16 }}>Always on</Text>
                ) : (
                  <Box
                    onClick={() => {
                      const current = config.enabled_stages || ['registration', 'inspection', 'quote', 'completed'];
                      const next = enabled ? current.filter((s: string) => s !== stage.key) : [...current, stage.key];
                      const updated = { ...config, enabled_stages: next };
                      setConfig(updated);
                      setSaving(true);
                      shopApi.update({ report_config: updated }).finally(() => setSaving(false));
                    }}
                    style={{
                      width: 44, height: 24, borderRadius: 12, cursor: 'pointer', transition: 'background 0.2s',
                      background: enabled ? BLUE : '#3F3F46',
                      display: 'flex', alignItems: 'center', padding: 2, flexShrink: 0, marginLeft: 16,
                      opacity: saving ? 0.5 : 1,
                    }}
                  >
                    <Box style={{ width: 20, height: 20, borderRadius: 10, background: '#fff', transition: 'transform 0.2s', transform: enabled ? 'translateX(20px)' : 'translateX(0)' }} />
                  </Box>
                )}
              </Box>
            );
          })}
        </Stack>
      </Section>

      <Section title="SMS Templates">
        <Text size="sm" c={TEXT_SECONDARY} mb={16}>
          Customize the SMS message sent to customers at each stage transition. Use {'{customer}'}, {'{shop}'}, {'{vehicle}'}, {'{report_url}'} as placeholders.
        </Text>
        <Stack gap={12}>
          {[
            { key: 'inspection', label: 'Check-In → Inspection' },
            { key: 'quote', label: 'Inspection → Quote' },
            { key: 'completed', label: 'Quote → Complete' },
          ].map((t) => (
            <Box key={t.key}>
              <Text size="xs" fw={600} c={TEXT_SECONDARY} mb={4}>{t.label}</Text>
              <Textarea
                value={(config.sms_templates || {})[t.key] || DEFAULT_SMS_TEMPLATES[t.key] || ''}
                onChange={(e) => setConfig((c: ReportConfig) => ({ ...c, sms_templates: { ...(c.sms_templates || {}), [t.key]: e.currentTarget.value } }))}
                onBlur={() => { setSaving(true); shopApi.update({ report_config: config }).finally(() => setSaving(false)); }}
                autosize
                minRows={2}
                maxRows={5}
                styles={inputStyles}
              />
            </Box>
          ))}
        </Stack>
      </Section>
    </>
  );
}

// ─── PRICE AGENT TAB ─────────────────────────────────────────────────────────

function PriceAgentTab() {
  const [settings, setSettings] = useState<PriceAgentSettings | null>(null);

  useEffect(() => {
    priceAgentSettingsApi.get().then(setSettings).catch(() => {});
  }, []);

  if (!settings) return <Text c={TEXT_MUTED} size="sm">Loading…</Text>;

  const lastSeen = settings.last_seen_at ? new Date(settings.last_seen_at) : null;
  const onlineSecondsAgo = lastSeen ? Math.floor((Date.now() - lastSeen.getTime()) / 1000) : null;
  const isOnline = onlineSecondsAgo != null && onlineSecondsAgo < 90;

  return (
    <Section title="Price Agent">
      <Stack gap="md">
        <Text size="sm" c={TEXT_MUTED}>
          The price agent runs on a dedicated shop PC with the HAIST Chrome extension. Sign in to HAIST in that extension and it'll pick up vendor-pricing jobs you queue from the Job page (<strong>Get vendor pricing</strong>).
        </Text>

        <Box style={{ padding: '8px 12px', borderRadius: 6, background: settings.enabled ? 'rgba(34,197,94,0.08)' : 'rgba(113,113,122,0.08)' }}>
          <Group gap={6}>
            <Box style={{ width: 8, height: 8, borderRadius: '50%', background: settings.enabled ? (isOnline ? '#22c55e' : '#f59e0b') : '#71717a' }} />
            <Text size="sm" c={TEXT}>
              {!settings.enabled && 'Price agent is disabled for this shop'}
              {settings.enabled && isOnline && 'Agent online'}
              {settings.enabled && !isOnline && (lastSeen ? `Agent offline · last seen ${lastSeen.toLocaleString()}` : 'Agent never connected — sign in to HAIST in the extension on your shop PC')}
            </Text>
          </Group>
          <Text size="xs" c={TEXT_MUTED} mt={4}>
            To enable / disable the agent for this shop, contact support.
          </Text>
        </Box>
      </Stack>
    </Section>
  );
}

// ─── SettingsPage ─────────────────────────────────────────────────────────────

export function SettingsPage() {
  const [tab, setTab] = useState<Tab>('account');

  return (
    <Box style={{ height: '100%', background: BG, display: 'flex', overflow: 'hidden' }}>
      {/* Left nav */}
      <Box style={{ width: NAV_W, flexShrink: 0, borderRight: `1px solid ${BORDER}`, display: 'flex', flexDirection: 'column', padding: '24px 0' }}>
        <Text size="xs" fw={600} c={TEXT_MUTED} mb={10} style={{ textTransform: 'uppercase', letterSpacing: 0.7, padding: '0 16px' }}>Settings</Text>
        {NAV_ITEMS.map((item) => {
          const active = tab === item.value;
          return (
            <Box
              key={item.value}
              onClick={() => setTab(item.value)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 16px',
                cursor: 'pointer',
                borderLeft: active ? `2px solid ${BLUE}` : '2px solid transparent',
                marginLeft: -1,
                background: active ? 'rgba(37,99,235,0.08)' : 'transparent',
                color: active ? TEXT : TEXT_MUTED,
                fontWeight: active ? 600 : 400,
                fontSize: 13,
                transition: 'color 0.12s, background 0.12s',
              }}
              onMouseEnter={(e) => { if (!active) (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.04)'; (e.currentTarget as HTMLElement).style.color = TEXT; }}
              onMouseLeave={(e) => { if (!active) { (e.currentTarget as HTMLElement).style.background = 'transparent'; (e.currentTarget as HTMLElement).style.color = TEXT_MUTED; } }}
            >
              {item.icon}
              {item.label}
            </Box>
          );
        })}
      </Box>

      {/* Content */}
      <Box style={{ flex: 1, overflowY: 'auto' }}>
        <Box style={{ maxWidth: 600, margin: '0 auto', padding: '28px 32px 48px' }}>
          {tab === 'account' && <AccountTab />}
          {tab === 'shop' && <ShopTab />}
          {tab === 'team' && <TeamTab />}
          {tab === 'features' && <FeaturesTab />}
          {tab === 'templates' && <TemplatesTab />}
          {tab === 'report' && <ReportTab />}
          {tab === 'price_agent' && <PriceAgentTab />}
        </Box>
      </Box>
    </Box>
  );
}
